import type { CirclesChart } from '../circlesChart.js';
import type { CirclesVariant } from '../gameTypes.js';
import { CIRCLES_VARIANTS, parseCirclesVariantSlug } from '../gameTypes.js';
import { ON_AND_ON_CIRCLES_VARIANTS } from './01-on-and-on.js';
import { SKY_HIGH_CIRCLES_VARIANTS } from './02-sky-high.js';
import { HEROES_CIRCLES_VARIANTS } from './03-heroes.js';
import { CRADLES_CIRCLES_VARIANTS } from './04-cradles.js';
import { MY_HEART_CIRCLES_VARIANTS } from './05-my-heart.js';
import { INVINCIBLE_CIRCLES_VARIANTS } from './06-invincible.js';
import { HOPE_CIRCLES_VARIANTS } from './07-hope.js';
import { ADVENTURE_CIRCLES_VARIANTS } from './08-adventure.js';
import { BLANK_CIRCLES_VARIANTS } from './09-blank.js';
import { DIAMOND_CIRCLES_VARIANTS } from './10-diamond.js';
import { HOLLAH_CIRCLES_VARIANTS } from './11-hollah.js';
import { NEKOZILLA_CIRCLES_VARIANTS } from './12-nekozilla.js';

/**
 * One playable Circles map: a single (track, variant) pair. Mirrors
 * the Pulse-mode {@link import('../builtinPulseMaps/index.js').BuiltinMap}
 * but typed to {@link CirclesChart} so consumers (the seeder, the home
 * page, the play page) get the narrowed mode-specific shape directly.
 *
 * Circles maps don't ship with deterministic expected replays today —
 * the engine accepts pointer input and the verifier will be added in
 * Phase 7. For symmetry with Pulse's map list we still expose the
 * `expectedReplay` slot on the same shape; it's `null` for now.
 */
export interface CirclesBuiltinMap {
  chart: CirclesChart;
  expectedReplay: null;
}

export interface CirclesBuiltinTrack {
  baseSlug: string;
  variants: Record<CirclesVariant, CirclesBuiltinMap>;
}

const VARIANT_BUNDLES: ReadonlyArray<{
  baseSlug: string;
  charts: Record<CirclesVariant, CirclesChart>;
}> = [
  // Order intentionally matches the Pulse catalogue so the home page
  // (and tier 1..5 progression labels) line up across modes.
  { baseSlug: 'on-and-on', charts: ON_AND_ON_CIRCLES_VARIANTS },
  { baseSlug: 'hope', charts: HOPE_CIRCLES_VARIANTS },
  { baseSlug: 'adventure', charts: ADVENTURE_CIRCLES_VARIANTS },
  { baseSlug: 'sky-high', charts: SKY_HIGH_CIRCLES_VARIANTS },
  { baseSlug: 'cradles', charts: CRADLES_CIRCLES_VARIANTS },
  { baseSlug: 'diamond', charts: DIAMOND_CIRCLES_VARIANTS },
  { baseSlug: 'blank', charts: BLANK_CIRCLES_VARIANTS },
  { baseSlug: 'heroes', charts: HEROES_CIRCLES_VARIANTS },
  { baseSlug: 'hollah', charts: HOLLAH_CIRCLES_VARIANTS },
  { baseSlug: 'my-heart', charts: MY_HEART_CIRCLES_VARIANTS },
  { baseSlug: 'nekozilla', charts: NEKOZILLA_CIRCLES_VARIANTS },
  { baseSlug: 'invincible', charts: INVINCIBLE_CIRCLES_VARIANTS },
];

export const BUILTIN_CIRCLES_TRACKS: CirclesBuiltinTrack[] = VARIANT_BUNDLES.map(
  (bundle) => ({
    baseSlug: bundle.baseSlug,
    variants: Object.fromEntries(
      CIRCLES_VARIANTS.map((v) => [
        v,
        {
          chart: bundle.charts[v],
          expectedReplay: null,
        } satisfies CirclesBuiltinMap,
      ]),
    ) as Record<CirclesVariant, CirclesBuiltinMap>,
  }),
);

export const BUILTIN_CIRCLES_MAPS: CirclesBuiltinMap[] = BUILTIN_CIRCLES_TRACKS.flatMap(
  (track) => CIRCLES_VARIANTS.map((v) => track.variants[v]),
);

/**
 * Look up a Circles builtin by slug (`'invincible'`,
 * `'invincible-hard'`, `'invincible-extreme'`). Mirrors
 * {@link import('../builtinPulseMaps/index.js').getBuiltinPulseMapBySlug} for
 * Pulse and applies the same literal-then-variant resolution order so
 * a hypothetical track whose slug ends in `-hard` won't be misparsed.
 */
export function getBuiltinCirclesMapBySlug(slug: string): CirclesBuiltinMap | undefined {
  const literalTrack = BUILTIN_CIRCLES_TRACKS.find((t) => t.baseSlug === slug);
  if (literalTrack) {
    return literalTrack.variants.normal;
  }
  const { baseSlug, variant } = parseCirclesVariantSlug(slug);
  const track = BUILTIN_CIRCLES_TRACKS.find((t) => t.baseSlug === baseSlug);
  if (!track) {
    return undefined;
  }
  return track.variants[variant];
}

export function listBuiltinCirclesTracks(): CirclesBuiltinTrack[] {
  return BUILTIN_CIRCLES_TRACKS;
}

export function listBuiltinCirclesMaps(): CirclesBuiltinMap[] {
  return BUILTIN_CIRCLES_MAPS;
}
